import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

/**
 * Passwordless email-link landing for approved applicants.
 * The applicant clicks the magic link in their approval email
 * (`{base}/access/<app_id>`). We exchange the app_id for a short-lived
 * demo cookie and route them to their role-specific dashboard.
 *
 *   investor    → /investor-demo (pitch screen → personalised welcome → demo)
 *   buyer/imp.  → /admin (Buyer Dashboard, curated 5 tabs)
 *   exporter    → /admin (Exporter Dashboard, curated 4 tabs)
 *   downstream  → /admin (Downstream Buyer Dashboard, curated 4 tabs)
 */
export default function AccessByAppId() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(true);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!appId) { setError("Missing access token"); setWorking(false); return; }
    // Guard: clear any sticky demo state from a previous session before minting a new one.
    try {
      localStorage.removeItem("investorDemoMode");
      localStorage.removeItem("investorDemoVia");
    } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/public/passwordless-login`,
          { app_id: appId },
          { withCredentials: true }
        );
        if (!data?.ok) throw new Error("Login failed");
        try {
          localStorage.setItem("investorDemoMode", "1");
          if (data.via) localStorage.setItem("investorDemoVia", data.via);
        } catch { /* ignore */ }
        // Route by role
        if (data.via_role === "investor") {
          navigate(`/investor-demo?token=auto&via=${encodeURIComponent(data.via)}`, { replace: true });
        } else {
          navigate("/admin", { replace: true });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unable to verify your access";
        setError(typeof detail === "string" ? detail : "Unable to verify your access");
        setWorking(false);
      }
    })();
  }, [appId, navigate]);

  return (
    <div className="min-h-screen bg-[#050A07] text-white flex items-center justify-center px-6" data-testid="access-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6 flex justify-center">
            <BrandLogo as="div" size="md" />
          </div>
          {working && !error && (
            <>
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-5 h-5 text-[#22C55E] animate-spin" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-bold" data-testid="access-loading-title">Verifying your access…</h1>
              <p className="text-[#A1B2A8] text-sm mt-2">
                Signing you in with your registered email — no password needed.
              </p>
            </>
          )}
          {!working && error && (
            <>
              <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-bold" data-testid="access-error-title">Access pending</h1>
              <p className="text-[#A1B2A8] text-sm mt-2" data-testid="access-error-msg">{error}</p>
              <p className="text-[#A1B2A8] text-xs mt-4">
                If you've just submitted your application, our team usually approves within a business day.
                You'll get an email at your registered address — please use the link inside that email to sign in.
              </p>
              <a href="/" className="inline-block mt-6 text-[#22C55E] hover:text-white text-sm" data-testid="access-back-home">
                ← Back to home
              </a>
            </>
          )}
          {!working && !error && (
            <>
              <CheckCircle2 className="w-12 h-12 text-[#22C55E] mx-auto mb-4" />
              <h1 className="font-['Outfit'] text-2xl font-bold">Signed in. Redirecting…</h1>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
